import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Leaf } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('biolosophy_chat_session');
  if (!id) {
    id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('biolosophy_chat_session', id);
  }
  return id;
};

export const ChatWidget = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      fetch(`${API}/api/assistant/history/${getSessionId()}`)
        .then((r) => r.json())
        .then((d) => setMessages(d.messages || []))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${API}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  ...next[next.length - 1],
                  content: next[next.length - 1].content + parsed.token,
                };
                return next;
              });
            } else if (parsed.error) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: t({
                    it: 'Ops, qualcosa è andato storto. Riprova o scrivici a info@biolosophy.it.',
                    en: 'Oops, something went wrong. Try again or write to info@biolosophy.it.',
                  }),
                };
                return next;
              });
            }
          } catch (e) { /* skip malformed chunk */ }
        }
      }
    } catch (e) {
      console.error('Chat error:', e);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#4A5D23] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#3d4d1d] transition-colors"
        aria-label="Chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-6 z-50 w-[350px] max-w-[calc(100vw-3rem)] h-[480px] bg-white border border-[#E5E0D8] shadow-2xl flex flex-col"
        >
          <div className="bg-[#1C1917] text-white px-5 py-4 flex items-center gap-3">
            <Leaf className="w-5 h-5 text-[#4A5D23]" />
            <div>
              <p className="font-medium text-sm">{t({ it: 'Assistente biolosophy', en: 'biolosophy Assistant' })}</p>
              <p className="text-xs text-white/60">{t({ it: 'Chiedimi tutto sul brand', en: 'Ask me anything about the brand' })}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]">
            {messages.length === 0 && (
              <div className="text-center text-sm text-[#57534E] py-8 px-4">
                {t({
                  it: 'Ciao! 🌿 Sono l\'assistente biolosophy. Posso aiutarti con taglie, spedizioni, Biolosophy Value e molto altro.',
                  en: 'Hi! 🌿 I\'m the biolosophy assistant. I can help with sizes, shipping, Biolosophy Value and more.',
                })}
              </div>
            )}
            {messages.map((m, idx) => (
              <div
                key={idx}
                data-testid={`chat-message-${m.role}-${idx}`}
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'ml-auto bg-[#4A5D23] text-white'
                    : 'mr-auto bg-white border border-[#E5E0D8] text-[#1C1917]'
                }`}
              >
                {m.content || (streaming && idx === messages.length - 1 ? '…' : '')}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-[#E5E0D8] p-3 flex gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              data-testid="chat-input"
              placeholder={t({ it: 'Scrivi un messaggio...', en: 'Type a message...' })}
              className="flex-1 px-3 py-2 border border-[#E5E0D8] text-sm focus:border-[#4A5D23] outline-none"
            />
            <button
              onClick={sendMessage}
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="w-10 h-10 bg-[#1C1917] text-white flex items-center justify-center hover:bg-[#4A5D23] transition-colors disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
